import React, {useState, useEffect} from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeft, 
  Trash2, 
  CheckCircle, 
  XCircle,
  Phone,
  Mail,
  Calendar,
  Star,
  Car,
  MapPin,
  AlertCircle,
  X,
  Eye,
  RotateCcw,
  UserX
} from 'lucide-react';
import { api } from '../../utils/api';
import { confirmPermanentDelete } from '../../utils/confirmPermanentDelete';
import { useAuth } from '../../contexts/AuthContext';

const TripLocationViewer = React.memo(({ startLocation, endLocation, tripId }) => {
  const [showLocations, setShowLocations] = useState(false);
  const [startLocationName, setStartLocationName] = useState(null);
  const [endLocationName, setEndLocationName] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const getLocationName = async (location, type = '') => {

    // Handle both array format [longitude, latitude] and object format {latitude, longitude}
    let latitude, longitude;

    if (Array.isArray(location) && location.length >= 2) {
      // Array format: [longitude, latitude] (MongoDB/GeoJSON standard)
      longitude = location[0];
      latitude = location[1];
    } else if (location && typeof location === 'object') {
      // Object format: {latitude, longitude}
      latitude = location.latitude;
      longitude = location.longitude;
    } else {
      console.warn('Invalid location format:', location);
      return null;
    }

    if (!latitude || !longitude) {
      console.warn('Missing latitude or longitude:', { latitude, longitude });
      return null;
    }

    try {
      // Use api utility which includes authentication
      const response = await api.post('/geocoding/reverse', {
        latitude: latitude,
        longitude: longitude,
      });

      const placeName = response?.placeName || response?.data?.placeName || 'Location not found';
      console.log('Final place name for', type, ':', placeName);
      return placeName;

    } catch (error) {
      console.error('Error in getLocationName for', type, ':', error);
      const fallback = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
      console.log('Using fallback location for', type, ':', fallback);
      return fallback;
    }
  };

  const handleViewLocations = async () => {
    // If we already have the data, toggle dialog
    if (startLocationName && endLocationName) {
      console.log('Using cached data, toggling dialog');
      setShowLocations(prev => !prev);
      return;
    }

    if (showLocations) {
      // Close dialog
      console.log('Closing dialog');
      setShowLocations(false);
      return;
    }

    // Start loading
    setIsLoading(true);

    try {
      // Fetch both locations
      const promises = [];

      if (!startLocationName && startLocation) {
        promises.push(
          getLocationName(startLocation).then(name => {
            console.log('Start location fetched:', name);
            return { type: 'start', name };
          })
        );
      }
      if (!endLocationName && endLocation) {
        promises.push(
          getLocationName(endLocation).then(name => {
            console.log('End location fetched:', name);
            return { type: 'end', name };
          })
        );
      }

      if (promises.length > 0) {
        console.log('Making API calls...');
        const results = await Promise.all(promises);

        console.log('API results received:', results);

        // Process all results first
        let newStartName = startLocationName;
        let newEndName = endLocationName;

        results.forEach(result => {
          if (result.type === 'start') {
            newStartName = result.name;
          } else if (result.type === 'end') {
            newEndName = result.name;
          }
        });

        console.log('Updating states with:', { newStartName, newEndName });

        // Update location names first
        setStartLocationName(newStartName);
        setEndLocationName(newEndName);
        setIsLoading(false);

        // Use setTimeout to ensure state updates are processed before showing modal
        setTimeout(() => {
          console.log('Opening modal after state update. Current state:', {
            startLocationName: newStartName,
            endLocationName: newEndName
          });
          setShowLocations(true);
        }, 100);

      } else {
        setShowLocations(true);
        setIsLoading(false);
      }

    } catch (error) {
      console.error('Error fetching location names:', error);
      setIsLoading(false);
      // Still show the dialog even if geocoding fails, but with timeout
      setTimeout(() => {
        console.log('Opening modal after error, showing coordinates only');
        setShowLocations(true);
      }, 100);
    }
  };

  const formatLocation = (location) => {
    // Handle both array format [longitude, latitude] and object format {latitude, longitude}
    let latitude, longitude;

    if (Array.isArray(location) && location.length >= 2) {
      // Array format: [longitude, latitude] (MongoDB/GeoJSON standard)
      longitude = location[0];
      latitude = location[1];
    } else if (location && typeof location === 'object') {
      // Object format: {latitude, longitude}
      latitude = location.latitude;
      longitude = location.longitude;
    } else {
      return 'Location not available';
    }

    if (!latitude || !longitude) {
      return 'Location not available';
    }

    return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <div className="space-y-1 text-sm">
          <div className="flex items-center text-gray-600">
            <span className="h-2 w-2 bg-green-500 rounded-full mr-2"></span>
            <span className="font-mono text-xs">{formatLocation(startLocation)}</span>
          </div>
          <div className="flex items-center text-gray-600">
            <span className="h-2 w-2 bg-red-500 rounded-full mr-2"></span>
            <span className="font-mono text-xs">{formatLocation(endLocation)}</span>
          </div>
        </div>
        <button
          onClick={handleViewLocations}
          disabled={isLoading}
          className={`px-3 py-1 text-xs rounded-md transition-colors ${isLoading
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
            }`}
        >
          {isLoading ? (
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 border border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
              <span>Loading...</span>
            </div>
          ) : (
            <Eye className="h-3 w-3" />
          )}
        </button>
      </div>

      {/* Location Names Dialog */}
      {showLocations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative">
            <button
              onClick={() => setShowLocations(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>

            <h3 className="text-lg font-semibold mb-4 text-gray-900">Trip Locations</h3>

            <div className="space-y-4">
              <div className="border-l-4 border-green-500 pl-4">
                <div className="flex items-center mb-1">
                  <span className="h-3 w-3 bg-green-500 rounded-full mr-2"></span>
                  <span className="text-sm font-medium text-gray-700">Start Location</span>
                </div>
                <p className="text-sm text-gray-900 font-medium">
                  {startLocationName || 'Loading...'}
                </p>
                <p className="text-xs text-gray-500 font-mono">
                  {formatLocation(startLocation)}
                </p>
              </div>

              <div className="border-l-4 border-red-500 pl-4">
                <div className="flex items-center mb-1">
                  <span className="h-3 w-3 bg-red-500 rounded-full mr-2"></span>
                  <span className="text-sm font-medium text-gray-700">End Location</span>
                </div>
                <p className="text-sm text-gray-900 font-medium">
                  {endLocationName || 'Loading...'}
                </p>
                <p className="text-xs text-gray-500 font-mono">
                  {formatLocation(endLocation)}
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowLocations(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

const PassengerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
    const [showPassengerJsonModal, setShowPassengerJsonModal] = useState(false);
  
    useEffect(() => {
      const anyModalOpen = showPassengerJsonModal;
      if (anyModalOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
  
      return () => {
        document.body.style.overflow = '';
      };
    }, [showPassengerJsonModal]);

  const { data, isLoading, error, refetch } = useQuery(
    ['passenger', id],
    () => api.get(`/passengers/${id}`),
    {
      enabled: !!id
    }
  );

    const updateDeveloperMutation = useMutation(
      (devFlag) => api.put(`/passengers/${id}/developer`, { dev: devFlag }),
      {
        onSuccess: (data, devFlag) => {
          toast.success(`Passenger developer flag ${devFlag ? 'enabled' : 'disabled'} successfully`);
          queryClient.invalidateQueries(['passenger', id]);
        },
        onError: () => {
          toast.error('Failed to update developer flag');
        }
      }
    );

    const handleToggleDeveloper = () => {
    const newDevFlag = !passenger.dev;
    updateDeveloperMutation.mutate(newDevFlag);
  };

  const blockMutation = useMutation(
    ({ isBlocked }) => api.patch(`/passengers/${id}/block`, { isBlocked }),
    {
      onSuccess: (_, { isBlocked }) => {
        toast.success(`Passenger ${isBlocked ? 'blocked' : 'unblocked'} successfully`);
        refetch();
      },
      onError: () => {
        toast.error('Failed to update passenger status');
      }
    }
  );

  const deleteMutation = useMutation(
    () => api.delete(`/passengers/${id}`),
    {
      onSuccess: () => {
        toast.success('Passenger deleted successfully');
        navigate('/passengers');
      },
      onError: () => {
        toast.error('Failed to delete passenger');
      }
    }
  );

  const handleBlock = (isBlocked) => {
    blockMutation.mutate({ isBlocked });
  };

  // A deleted account cannot log in: the app answers 403 ACCOUNT_DELETED. Revoking undoes it.
  const restoreMutation = useMutation(
    () => api.put(`/passengers/${id}/restore-account`),
    {
      onSuccess: () => {
        toast.success('Passenger account restored');
        refetch();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || 'Failed to restore passenger account');
      }
    }
  );

  const markDeletedMutation = useMutation(
    () => api.put(`/passengers/${id}/mark-deleted`, {}),
    {
      onSuccess: () => {
        toast.success('Passenger account marked as deleted');
        refetch();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || 'Failed to mark passenger account as deleted');
      }
    }
  );

  const handleRestore = () => {
    if (window.confirm('Restore this account? The passenger will be able to log in again with their trip history.')) {
      restoreMutation.mutate();
    }
  };

  const handleMarkDeleted = () => {
    if (window.confirm('Mark this account as deleted? The passenger will not be able to log in. You can restore it later.')) {
      markDeletedMutation.mutate();
    }
  };

  const handleDelete = () => {
    if (confirmPermanentDelete('this passenger record', 'To block login and keep the record, use "Mark as deleted" instead.')) {
      deleteMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <AlertCircle className="empty-state-icon" />
        <h3 className="empty-state-title">Passenger not found</h3>
        <p className="empty-state-description">
          The passenger you're looking for doesn't exist.
        </p>
        <Link to="/passengers" className="btn btn-primary mt-4">
          Back to Passengers
        </Link>
      </div>
    );
  }

  const passenger = data?.data?.data?.passenger;
  const statistics = data?.data?.data?.statistics;
  const recentTrips = data?.data?.data?.recentTrips || [];

  if (!passenger) {
    return null;
  }

  const getStatusBadge = () => {
    if (passenger.isDeleted === true) {
      return <span className="badge badge-danger">Deleted</span>;
    }
    if (passenger.isBlocked) {
      return <span className="badge badge-danger">Blocked</span>;
    }
    return <span className="badge badge-success">Active</span>;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => {
                  const searchParams = new URLSearchParams(window.location.search);
                  const fromSource = searchParams.get('from');
                  const sourceId = searchParams.get('sourceId');
                  
                  // Navigate back to the source page
                  if (fromSource === 'trip' && sourceId) {
                    navigate(`/trips/${sourceId}`);
                  } else {
                    navigate('/passengers');
                  }
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div>
                <h1 className="page-title">{passenger.name || 'Unnamed Passenger'}</h1>
                <p className="page-subtitle">Passenger Details</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
{hasPermission('passengers', 'write') && (
                <button
                  onClick={handleToggleDeveloper}
                  disabled={updateDeveloperMutation.isLoading}
                  className={`
    relative flex items-center gap-3 pl-2 pr-4 py-1 rounded-full transition-all w-auto bg-gray-200
    ${updateDeveloperMutation.isLoading ? "opacity-60 cursor-wait" : "cursor-pointer"}
    ${passenger.dev ? "border" : "border"}
  `}
                >
                  {/* Spinner */}
                  {updateDeveloperMutation.isLoading && (
                    <span className="loading loading-spinner loading-sm"></span>
                  )}

                  {/* Toggle Track */}
                  <span
                    className={`
      relative inline-flex items-center w-12 h-6 rounded-full shadow-inner
       ${passenger.dev ? "bg-green-700" : "bg-gray-600"}
    `}
                  >
                    {/* Toggle Circle */}
                    <span
                      className={`
        absolute w-5 h-5 rounded-full shadow-md transform transition-all
        ${passenger.dev ? "translate-x-6 bg-white" : "translate-x-1 bg-white"}
      `}
                    ></span>
                  </span>

                  {/* Label */}
                  <span className="text-sm font-medium text-black whitespace-nowrap">
                    {passenger.dev ? "Developer" : "Not Developer"}
                  </span>
                </button>
              )}
              {hasPermission('passengers', 'write') && (
                <button
                  onClick={() => handleBlock(!passenger.isBlocked)}
                  disabled={blockMutation.isLoading}
                  className={`btn ${passenger.isBlocked ? 'btn-success' : 'btn-warning'}`}
                >
                  {passenger.isBlocked ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Unblock
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 mr-2" />
                      Block
                    </>
                  )}
                </button>
              )}
              
              {hasPermission('passengers', 'write') && passenger.isDeleted === true && (
                <button
                  onClick={handleRestore}
                  disabled={restoreMutation.isLoading}
                  className="btn btn-success"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Restore account
                </button>
              )}

              {hasPermission('passengers', 'delete') && passenger.isDeleted !== true && (
                <button
                  onClick={handleMarkDeleted}
                  disabled={markDeletedMutation.isLoading}
                  className="btn btn-warning"
                  title="Blocks login but keeps the record. Can be restored."
                >
                  <UserX className="h-4 w-4 mr-2" />
                  Mark as deleted
                </button>
              )}

              {hasPermission('passengers', 'delete') && (
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isLoading}
                  className="btn btn-danger"
                  title="Removes the record for good"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete permanently
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Account deletion */}
      {passenger.isDeleted === true && (
        <div className="card border border-red-200">
          <div className="card-header">
            <h3 className="text-lg font-medium text-red-700">Account deleted</h3>
          </div>
          <div className="card-body">
            <p className="text-sm text-gray-700 mb-3">
              This passenger cannot log in: the app answers "This account has been deleted".
              Restoring the account lets them log in again with their trip history.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Deleted on</label>
                <p className="text-sm text-gray-900">
                  {passenger.deletedAt ? new Date(passenger.deletedAt).toLocaleString() : 'Unknown'}
                </p>
              </div>
              <div>
                <label className="form-label">Reason</label>
                <p className="text-sm text-gray-900">{passenger.deleteReason || 'Not given'}</p>
              </div>
              <div>
                <label className="form-label">Deleted by</label>
                <p className="text-sm text-gray-900">{passenger.deletedBy || 'The passenger, in the app'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {passenger.isDeleted !== true && passenger.deletionRevokedAt && (
        <div className="text-sm text-gray-500">
          Account deletion was revoked on {new Date(passenger.deletionRevokedAt).toLocaleString()}
          {passenger.deletionRevokedBy ? ` by ${passenger.deletionRevokedBy}` : ''}.
        </div>
      )}

      {/* Passenger Information */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Basic Information */}
        <div className="card">
             <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              {/* <button
                onClick={() => setShowPassengerJsonModal(true)}
                className='font-medium bg-gray-100 px-2 rounded border'
              >
                {"{}"}
              </button> */}
            </div>
          </div>
          <div className="card-body space-y-4">             <div className="flex items-center space-x-3">
              <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-xl font-medium text-primary-700">
                  {(passenger.name || 'Passenger').toString().charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900">{passenger.name || 'Unnamed Passenger'}</h4>
                <p className="text-sm text-gray-500 capitalize">{passenger.gender || 'Not specified'}</p>
                {getStatusBadge()}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Phone className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">{passenger.phone || 'N/A'}</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">{passenger.email || 'N/A'}</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">
                  Joined {passenger.createdOn ? new Date(passenger.createdOn).toLocaleDateString() : 'N/A'}
                </span>
              </div>

                {passenger.deviceMeta && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Operating System</span>
                <span className="text-sm font-medium text-gray-900">
                   {passenger.deviceMeta.os ? (passenger.deviceMeta.os.charAt(0).toUpperCase() + passenger.deviceMeta.os.slice(1)) : 'Unknown'}
                </span>
              </div>
                )}

              {passenger.deviceMeta && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">App Version</span>
                <span className="text-sm font-medium text-gray-900">
                  {passenger.deviceMeta.appVersion || 'Unknown'}
                </span>
              </div>
              )}

            </div>
          </div>
        </div>

        {/* Trip Statistics */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Trip Statistics</h3>
          </div>
          <div className="card-body space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Trips</span>
              <span className="text-sm font-medium text-gray-900">
                {statistics?.totalTrips || passenger.totalTrips || 0}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Completed Trips</span>
              <span className="text-sm font-medium text-gray-900">
                {statistics?.completedTrips || passenger.completedTrips || 0}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Cancelled Trips</span>
              <span className="text-sm font-medium text-gray-900">
                {statistics?.cancelledTrips || passenger.cancelledTrips || 0}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Spent</span>
              <span className="text-sm font-medium text-gray-900">
                ₹{(statistics?.totalSpent || passenger.totalSpent || 0).toFixed(2)}
              </span>
            </div>
            
            {(passenger.lastTripDate || recentTrips[0]?.createdOn) && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Last Trip</span>
                <span className="text-sm font-medium text-gray-900">
                  {new Date(passenger.lastTripDate || recentTrips[0]?.createdOn).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Rating Information */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Rating</h3>
          </div>
          <div className="card-body space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Current Rating</span>
              <div className="flex items-center space-x-2">
                <Star className="h-4 w-4 text-yellow-400" />
                <span className="text-sm font-medium text-gray-900">
                  {passenger.ratingData?.currentrating?.toFixed(1) || '0.0'}
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Reviews</span>
              <span className="text-sm font-medium text-gray-900">
                {passenger.ratingData?.count || 0}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Rating</span>
              <span className="text-sm font-medium text-gray-900">
                {passenger.ratingData?.total || 0}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Preferred Payment</span>
              <span className="text-sm font-medium text-gray-900 capitalize">
                {passenger.preferredPaymentMethod || 'Not set'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Trips */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Recent Trips</h3>
        </div>
        <div className="card-body">
          {recentTrips.length === 0 ? (
            <div className="empty-state">
              <Car className="empty-state-icon" />
              <h3 className="empty-state-title">No recent trips</h3>
              <p className="empty-state-description">
                This passenger hasn't taken any trips yet.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Trip ID</th>
                    <th className="table-header-cell">Route</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Fare</th>
                    <th className="table-header-cell">Created</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {recentTrips.map((trip) => (
                    <tr key={trip._id} className="table-row">
                      <td className="table-cell">
                        <div className="text-sm font-medium text-gray-900">
                          {trip._id}
                        </div>
                      </td>
                      <td className="table-cell">
                        <TripLocationViewer
                            key={trip._id}
                            tripId={trip._id}
                            startLocation={trip.startLocation}
                            endLocation={trip.endLocation}
                          />
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${
                          trip.status === 'COMPLETED' ? 'badge-success' :
                          trip.status === 'CANCELLED' ? 'badge-danger' :
                          'badge-warning'
                        }`}>
                          {trip.status}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm font-medium text-gray-900">
                          {(trip.status === 'COMPLETED' || trip.status === 'DIVERGED') && trip.finalFare ? (
                            <span className="text-green-600">₹{trip.finalFare.toFixed(2)}</span>
                          ) : trip.minFare && trip.maxFare ? (
                            `₹${trip.minFare} - ₹${trip.maxFare}`
                          ) : trip.minFare ? (
                            `₹${trip.minFare}`
                          ) : trip.maxFare ? (
                            `₹${trip.maxFare}`
                          ) : (
                            'N/A'
                          )}
                        </div>
                        {(trip.status === 'COMPLETED' || trip.status === 'DIVERGED') && trip.finalFare && (
                          <div className="text-xs text-gray-500">Final Fare</div>
                        )}
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">
                          {new Date(trip.bookingTime || trip.createdOn || trip.createdAt).toLocaleDateString()}
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(trip.bookingTime || trip.createdOn || trip.createdAt).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="table-cell">
                        <Link
                          to={`/trips/${trip._id}`}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

       {showPassengerJsonModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Passenger Details (JSON)</h3>
                    <button
                      onClick={() => setShowPassengerJsonModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="mb-4 text-sm text-gray-700">
                    <p>Full driver object (JSON).</p>
                  </div>
                  <div className="bg-gray-100 p-4 rounded overflow-auto" style={{ maxHeight: '60vh' }}>
                    <pre className="text-xs">{JSON.stringify(passenger, null, 2)}</pre>
                  </div>
                  <div className="flex justify-end mt-4">
                    <button
                      onClick={() => setShowPassengerJsonModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

      {/* Block Information */}
      {passenger.isBlocked && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Block Information</h3>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {passenger.blockReason && (
                <div>
                  <label className="form-label">Reason</label>
                  <p className="text-sm text-gray-900">{passenger.blockReason}</p>
                </div>
              )}
              
              {passenger.blockExpiry && (
                <div>
                  <label className="form-label">Block Expires</label>
                  <p className="text-sm text-gray-900">
                    {new Date(passenger.blockExpiry).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PassengerDetails; 